import React, { useMemo, useState } from 'react';
import { Button, Tag } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { PlusCircleOutlined } from '@ant-design/icons';
import Table from '../../Common/Table';
import {
  useUsersTableGet,
  UserTableRow,
} from '../../../hooks/api/users';
import { createDefaultHiddenColumns, getSorterParams, queryFilterParams } from '../../../utils';
import { useMessageError } from '../../../hooks/common';
import { useAuth, UserStatusType } from '../../../store/auth';
import { UserRole, UserRoleDisplay, UserStatusDisplay } from '../../../enums/user';
import { dateTimeFormat } from '../../../contstant';
import {
  DeactivateButton, DeleteButton, ResetPasswordButton,
} from './actions';
import { UserStatusColor } from '../Users';

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || 'rgba(0,0,0,.15)';
}

const columns: ProColumns<UserTableRow>[] = [
  /* {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    renderText: (_, row) => row.fullName,
  }, */
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: true,
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'roles', // key - for filters. dataIndex - for display.
    sorter: true,
    valueEnum: UserRoleDisplay,
    hideInSearch: true,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Tag color={getUserStatusColor(record.status)} className="capitalize-first-letter">{record.status}</Tag>
    ),
  },
  /* {
    title: 'Date of birth',
    dataIndex: 'dateOfBirth',
    key: 'dateOfBirthdateOfBirth',
    hideInSearch: true,
    // valueType: 'dateRange',
    sorter: false,
    // .utc(true)
    renderText: (text) => (dayjs(text).isValid() ? dayjs(text).format(dateFormat) : '-'),
  }, */
  {
    title: 'Created at',
    dataIndex: 'createdAt',
    key: 'createdAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: 'Updated at',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
];

function ToolBar() {
  return [
    <Link key="create" to="create">
      <Button
        key="add"
        icon={<PlusCircleOutlined />}
        type="primary"
      >
        Add
      </Button>
    </Link>,
  ];
}

export default function Admins(): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const usersGet = useUsersTableGet('admins');
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(Date.now());

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return usersGet.fetch({
      ...newParams,
      // roles: [UserRole.EDITOR, UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT],
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([usersGet]);

  const defaultHiddenColumns = useMemo(() => createDefaultHiddenColumns(
    ['createdAt', 'updatedAt'],
  ), []);

  return (
    <Table<UserTableRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className={clsx('transparent')}
      headerTitle="List of administrators"
      toolBarRender={user?.role && [UserRole.ROOT].includes(user?.role) ? ToolBar : undefined}
      request={tableRequest}
      columns={columns}
      actions={user?.role && [UserRole.ROOT].includes(user?.role) ? [
        ['edit', ({ record }) => ({
          title: 'Edit administrator',
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            navigate(`${record.id}/edit`);
          },
        })],
        [DeleteButton, {
          setTableKey,
        }],
        [DeactivateButton, { setTableKey }],
        [ResetPasswordButton],
      ] : undefined}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {
          // display: 'none',
        },
      }}
      options={{
        // search: {
        //   name: 'search',
        // },
      }}
      columnsState={{
        persistenceKey: 'pro-table-admins',
        persistenceType: 'localStorage',
        defaultValue: defaultHiddenColumns,
      }}
    />
  );
}
